import type { SemanticStateName } from '../semantic/index';

/**
 * Semantic state tokens — the presentation vocabulary for the mandated states.
 *
 * T-3 / AC-FE1W0-22 (FE-1 Stage Work Item §11.2 check 4):
 *   "every semantic distinction is carried textually or structurally, NOT by
 *    color or iconography alone."
 *
 * `label` is therefore REQUIRED, non-optional and non-empty by convention, and it
 * is the authoritative carrier of the distinction. `tone` is a hint: it is a role
 * name, it may be shared between states, and a component that renders tone without
 * label has not rendered the state.
 *
 * WHY THE KEYS ARE AXIS-QUALIFIED AND NOT THE EIGHT MANDATED NAMES
 *   The mandated eight names are not eight members of one vocabulary. `Unknown` in
 *   particular names TWO different states — presence UNKNOWN (the read plane
 *   resolved and data is absent) and freshness UNKNOWN (an answer exists but its
 *   currency cannot be established). Giving them one token would give them one
 *   label, which is the presentation-layer version of exactly the collapse
 *   `frontend/src/shared/semantic/invariants.ts` forbids in the type system.
 *
 *   So there are eleven tokens over three axes, and the two UNKNOWNs have
 *   deliberately different labels. `MANDATED_NAME_TO_TOKEN_KEYS` maps the mandated
 *   eight onto them, exhaustively, so the mapping is reviewable.
 *
 * NO TOKEN HERE ASSERTS AN OPERATIONAL FACT.
 *   `PRESENCE_AVAILABLE` says snapshot data was retrieved. It does NOT say the
 *   capability is operationally available, ready, assigned or committed, and its
 *   label is written so it cannot be read that way (FE-1 Stage Work Item §6.4).
 *   No availability-guarantee, commitment, readiness or assignment token exists —
 *   those vocabularies belong to a later authorized increment, and adding one here
 *   would put FND-P2W08-01 (RESERVED != Availability Guaranteed) at risk.
 */

/** What a state means structurally. A role, not a colour. */
export type SemanticTone = 'NEUTRAL' | 'INDETERMINATE' | 'CAUTION' | 'FAILURE' | 'ABSENCE';

export interface SemanticStateToken {
  /** MANDATORY. The authoritative textual carrier of the distinction. */
  readonly label: string;
  /** What the state means, for tooltips and assistive text. */
  readonly description: string;
  /** Visual hint ONLY. Never the sole carrier; may be shared between states. */
  readonly tone: SemanticTone;
}

/** The eleven tokens: three presence, four freshness, one authority, three conditions. */
export type SemanticStateTokenKey =
  | 'PRESENCE_AVAILABLE'
  | 'PRESENCE_UNKNOWN'
  | 'PRESENCE_UNAVAILABLE'
  | 'FRESHNESS_FRESH'
  | 'FRESHNESS_STALE'
  | 'FRESHNESS_UNKNOWN'
  | 'FRESHNESS_DEGRADED'
  | 'AUTHORITY_NON_AUTHORITY'
  | 'CONDITION_LOADING'
  | 'CONDITION_EMPTY'
  | 'CONDITION_ERROR';

export const SEMANTIC_STATE_TOKENS = Object.freeze({
  PRESENCE_AVAILABLE: {
    label: 'Definition available',
    description:
      'Snapshot data was retrieved for this subject. This is NOT a statement that the capability is operationally available, ready, committed or assigned.',
    tone: 'NEUTRAL',
  },
  PRESENCE_UNKNOWN: {
    label: 'Unknown',
    description:
      'The read plane resolved and no definition data is available for this subject. This is a normal absence of data, not an error and not an empty result.',
    tone: 'INDETERMINATE',
  },
  PRESENCE_UNAVAILABLE: {
    label: 'Unavailable',
    description:
      'A functioning reader could not supply a trustworthy response. No data is shown and no diagnostic is published.',
    tone: 'FAILURE',
  },
  FRESHNESS_FRESH: {
    label: 'Fresh',
    description: 'The answer is current.',
    tone: 'NEUTRAL',
  },
  FRESHNESS_STALE: {
    label: 'Stale',
    description:
      'The answer is aged. Stale inputs do not become an allow, an available or a success.',
    tone: 'CAUTION',
  },
  FRESHNESS_UNKNOWN: {
    label: 'Freshness unknown',
    description:
      'An answer exists, but how current it is cannot be established. This is a caveat on an answer, NOT an absent answer.',
    tone: 'INDETERMINATE',
  },
  FRESHNESS_DEGRADED: {
    label: 'Degraded',
    description: 'The answer is current but comes from a diminished source.',
    tone: 'CAUTION',
  },
  AUTHORITY_NON_AUTHORITY: {
    label: 'Non-authority',
    description:
      'A fixed property of every read model: this view describes state and never authorizes action.',
    tone: 'NEUTRAL',
  },
  CONDITION_LOADING: {
    label: 'Loading',
    description:
      'No read outcome has been obtained yet. This says nothing about the subject and must not be presented as unknown.',
    tone: 'NEUTRAL',
  },
  CONDITION_EMPTY: {
    label: 'Empty',
    description:
      'A resolved projection contained zero items. Never a substitute for unknown: on the Capability Definition read plane an empty source maps to unknown, not to empty.',
    tone: 'ABSENCE',
  },
  CONDITION_ERROR: {
    label: 'Error',
    description:
      'A frontend engineering failure. Not a read outcome: the frozen contract has no error status, and a reader failure is reported as unavailable.',
    tone: 'FAILURE',
  },
} satisfies Readonly<Record<SemanticStateTokenKey, SemanticStateToken>>);

/**
 * The mandated eight names mapped onto the tokens that carry them.
 * Exhaustive over `SemanticStateName`, so a name cannot be left unmapped.
 */
export const MANDATED_NAME_TO_TOKEN_KEYS = Object.freeze({
  Loading: ['CONDITION_LOADING'],
  Unknown: ['PRESENCE_UNKNOWN', 'FRESHNESS_UNKNOWN'],
  Empty: ['CONDITION_EMPTY'],
  Error: ['CONDITION_ERROR'],
  Stale: ['FRESHNESS_STALE'],
  Degraded: ['FRESHNESS_DEGRADED'],
  Unavailable: ['PRESENCE_UNAVAILABLE'],
  NonAuthority: ['AUTHORITY_NON_AUTHORITY'],
} as const satisfies Readonly<Record<SemanticStateName, readonly SemanticStateTokenKey[]>>);
